/**
 * Arithmétique décimale.
 *
 * Les montants d'une facture ne passent JAMAIS par un float : chaque valeur
 * lue dans l'XML est convertie en Decimal (decimal.js-light, précision 20).
 * Les comparaisons de totaux tolèrent uniquement l'arrondi légal déclaré
 * (BT-114) et une demi-unité de centime par agrégat.
 */
import Decimal from 'decimal.js-light';

Decimal.set({ precision: 20, rounding: Decimal.ROUND_HALF_UP });

export type Dec = Decimal;

const AMOUNT_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

/** Convertit une chaîne XML en Decimal. Retourne undefined si vide/invalide. */
export function parseDecimal(raw: string | null | undefined): Dec | undefined {
  if (raw == null) return undefined;
  const s = raw.trim();
  if (s === '' || !AMOUNT_RE.test(s)) return undefined;
  try {
    const d = new Decimal(s);
    return Number.isFinite(d.toNumber()) ? d : undefined;
  } catch {
    return undefined;
  }
}

/** Formate pour l'affichage français : « 1 234,56 € ». */
export function formatAmount(value: Dec | undefined, currency = 'EUR'): string {
  if (!value) return '';
  const fixed = value.toFixed(2);
  const negative = fixed.startsWith('-');
  const [intPart = '0', decPart = '00'] = fixed.replace('-', '').split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
  const symbol = currency === 'EUR' ? '\u00a0€' : `\u00a0${currency}`;
  return `${negative ? '-' : ''}${grouped},${decPart}${symbol}`;
}

/** « 1 234,56 » sans symbole. */
export function formatNumber(value: Dec | undefined): string {
  if (!value) return '';
  return formatAmount(value, '').replace(/\u00a0$/, '').trim();
}

export function sum(values: Array<Dec | undefined>): Dec {
  return values.reduce<Dec>((acc, v) => (v ? acc.plus(v) : acc), new Decimal(0));
}

/** Écart entre deux montants, tolérance = 0,005 × agrégats comparés. */
export function withinTolerance(a: Dec, b: Dec, tolerance = '0.01'): boolean {
  return a.minus(b).abs().lte(tolerance);
}
